<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  sections: {
    type: Array,
    default: () => [],
  },
  maxHeightClass: {
    type: String,
    default: 'max-h-[18rem]',
  },
  thumbnailSize: {
    type: Number,
    default: 20,
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  isSearching: {
    type: Boolean,
    default: false,
  },
  autofocusSearch: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();
const searchInput = ref(null);
const searchQuery = ref('');

const filteredSections = computed(() => {
  if (!searchQuery.value) {
    return props.sections;
  }

  const query = searchQuery.value.toLowerCase();

  return props.sections
    .map(section => ({
      ...section,
      items: (section.items || []).filter(item =>
        item.label.toLowerCase().includes(query)
      ),
    }))
    .filter(section => section.items.length > 0);
});

const shouldShowEmptyState = computed(
  () => filteredSections.value.length === 0 && !props.isSearching
);

onMounted(() => {
  if (props.showSearch && props.autofocusSearch && searchInput.value) {
    searchInput.value.focus();
  }
});
</script>

<template>
  <div
    class="flex min-w-[272px] flex-col gap-2 bg-inherit p-3"
    :class="{ 'pt-2': !showSearch }"
  >
    <div v-if="showSearch" class="shrink-0">
      <div class="relative">
        <span
          class="absolute left-3 top-1/2 i-lucide-search size-5 -translate-y-1/2 text-n-slate-11"
        />
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="search"
          :placeholder="searchPlaceholder"
          class="reset-base h-9 w-full rounded-xl border border-n-weak bg-transparent py-2 pl-12 pr-3 text-sm text-n-slate-12 outline-none focus:border-n-slate-7 dark:border-n-strong"
        />
      </div>
    </div>

    <div class="min-h-0 overflow-y-auto" :class="maxHeightClass">
      <template
        v-for="(section, sectionIndex) in filteredSections"
        :key="section.title || sectionIndex"
      >
        <div class="flex flex-col gap-1">
          <div
            v-if="section.title || section.badgeLabel"
            class="flex items-center justify-between gap-2 px-2 py-2"
          >
            <p
              v-if="section.title"
              class="mb-0 truncate font-['Inter'] text-sm font-semibold text-n-slate-11"
            >
              {{ section.title }}
            </p>
            <span
              v-if="section.badgeLabel"
              class="inline-flex shrink-0 items-center rounded-full bg-n-brand/10 px-2 py-0.5 text-[11px] font-medium text-n-brand"
            >
              {{ section.badgeLabel }}
            </span>
          </div>

          <button
            v-for="(item, itemIndex) in section.items"
            :key="`${item.value}-${itemIndex}`"
            type="button"
            class="inline-flex h-[34px] min-h-[34px] w-full min-w-0 items-center justify-start gap-2 rounded-lg border-0 px-2 py-[7px] text-n-slate-12 transition-all duration-200 ease-in-out hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2"
            :class="{
              'bg-n-alpha-1 dark:bg-n-solid-active': item.isSelected,
              [item.itemClass]: Boolean(item.itemClass),
            }"
            @click="emit('select', item)"
          >
            <Avatar
              v-if="item.thumbnail"
              :name="item.thumbnail.name"
              :src="item.thumbnail.src"
              :status="item.availabilityStatus"
              :size="thumbnailSize"
            />
            <span
              v-else-if="item.icon"
              class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md"
              :class="item.iconContainerClass"
            >
              <span :class="[item.icon, item.iconClass]" />
            </span>
            <span
              v-else-if="item.emoji"
              class="inline-flex h-5 w-5 shrink-0 items-center justify-center"
            >
              {{ item.emoji }}
            </span>
            <span class="min-w-0 truncate text-sm leading-5">
              {{ item.label }}
            </span>
          </button>

          <div
            v-if="sectionIndex < filteredSections.length - 1"
            class="mx-2 my-1 h-px bg-n-alpha-2"
          />
        </div>
      </template>

      <div v-if="props.isSearching" class="px-2 py-1.5 text-sm text-n-slate-11">
        {{ t('DROPDOWN_MENU.SEARCHING') }}
      </div>
      <div
        v-else-if="shouldShowEmptyState"
        class="px-2 py-1.5 text-sm text-n-slate-11"
      >
        {{ t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT') }}
      </div>
    </div>
  </div>
</template>
